/**
 * QrBackfillAdmin — admin one-button UI to mint SpLink QR codes for
 * every legacy inventory lot in the network in one pass.
 *
 * Route: /admin/foodpulse/qr-backfill
 *
 * Flow:
 *   1. Dry-run first — see how many lots are missing QRs.
 *   2. Big red "Run backfill" button processes up to 200 per call.
 *   3. Rerun to sweep the next batch until `remaining_estimate = 0`.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, PlayCircle, RefreshCw, Loader2, Info } from "lucide-react";
import { http } from "@/lib/api";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";
const AMBER = "#E4A140";
const PURPLE = "#7C3AED";

export default function QrBackfillAdmin() {
  const [dry, setDry]           = useState(null);
  const [result, setResult]     = useState(null);
  const [running, setRunning]   = useState(false);
  const [checking, setChecking] = useState(false);
  const [limit, setLimit]       = useState(200);
  const [err, setErr]           = useState(null);

  const runDry = async () => {
    setChecking(true); setErr(null);
    try {
      const r = await http.post("/inventory/admin/qr-backfill", null, { params: { limit, dry_run: true } });
      setDry(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Dry-run failed"); }
    setChecking(false);
  };
  const runReal = async () => {
    setRunning(true); setErr(null); setResult(null);
    try {
      const r = await http.post("/inventory/admin/qr-backfill", null, { params: { limit, dry_run: false } });
      setResult(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Backfill failed"); }
    setRunning(false);
  };

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="qr-backfill-admin">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-semibold mb-2" style={{ color: GREEN }}>
          <ArrowLeft size={12} /> Admin home
        </Link>
        <h1 className="text-2xl font-black" style={{ color: GREEN }}>Bulk QR Backfill</h1>
        <p className="text-sm text-gray-600 mb-6">
          Mint SpLink short URLs + QR codes for every legacy inventory lot that
          is missing one. Idempotent — runs skip lots that already have a QR.
          Each call processes up to <b>{limit}</b> lots.
        </p>

        {err && <div className="rounded-xl border p-3 mb-4 text-sm" style={{ borderColor: "#F0C0C0", background: "#FEE7DA", color: "#7A3E00" }} data-testid="err-banner">{err}</div>}

        <section className="rounded-2xl border p-5 bg-white mb-4" style={{ borderColor: "#E9D8B4" }}>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm inline-flex items-center gap-2">
              Batch size
              <input type="number" min={1} max={1000} value={limit} onChange={(e) => setLimit(Number(e.target.value) || 200)} className="border rounded-full px-3 py-1 text-sm w-24" data-testid="limit-input" />
            </label>
            <button onClick={runDry} disabled={checking} className="rounded-full border px-4 py-2 text-sm font-semibold inline-flex items-center gap-2" style={{ borderColor: GREEN, color: GREEN }} data-testid="dry-btn">
              {checking ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Dry-run — count lots missing QR
            </button>
            <button onClick={runReal} disabled={running} className="rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-2" style={{ background: AMBER, color: "white" }} data-testid="run-btn">
              {running ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
              Run backfill
            </button>
          </div>
        </section>

        {dry && (
          <section className="rounded-2xl border p-5 bg-white mb-4" style={{ borderColor: "#E9D8B4" }} data-testid="dry-result">
            <div className="text-sm font-bold mb-1 inline-flex items-center gap-1" style={{ color: PURPLE }}>
              <Info size={14} /> Dry-run
            </div>
            <div className="text-3xl font-black" style={{ color: GREEN }}>{dry.would_process}</div>
            <div className="text-xs text-gray-600">lots would be processed by the next backfill call.</div>
            {dry.sample?.length > 0 && (
              <ul className="mt-3 text-xs text-gray-700 list-disc pl-5">
                {dry.sample.map((s) => <li key={s.id}>{s.product || "—"} <span className="font-mono opacity-60">{s.id.slice(0, 8)}</span></li>)}
              </ul>
            )}
          </section>
        )}

        {result && (
          <section className="rounded-2xl border p-5 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="run-result">
            <div className="text-sm font-bold mb-1" style={{ color: GREEN }}>Backfill result</div>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div>
                <div className="text-xs text-gray-600">Processed</div>
                <div className="text-2xl font-black" style={{ color: GREEN }} data-testid="processed">{result.processed}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Failed</div>
                <div className="text-2xl font-black text-red-700" data-testid="failed">{result.failed_count}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600">Remaining (est)</div>
                <div className="text-2xl font-black" style={{ color: AMBER }} data-testid="remaining">{result.remaining_estimate}</div>
              </div>
            </div>
            {result.failed_sample?.length > 0 && (
              <details className="mt-3">
                <summary className="text-xs cursor-pointer text-gray-600">Show failed sample</summary>
                <ul className="mt-2 text-xs text-red-700 list-disc pl-5">
                  {result.failed_sample.map((f) => <li key={f.id}><span className="font-mono">{f.id.slice(0, 8)}</span> — {f.error}</li>)}
                </ul>
              </details>
            )}
            {result.remaining_estimate > 0 && (
              <div className="mt-4 text-xs text-gray-700">
                More lots pending — click <b>Run backfill</b> again to sweep the next batch.
              </div>
            )}
          </section>
        )}
      </div>
    </main>
  );
}
